import { Timestamp } from "firebase/firestore";

export enum ComponentType {
    EMAIL_INPUT = 'email_input',
    TEXT_INPUT = 'text_input',
    TEXTAREA = 'textarea',
    MULTIPLE_CHOICE = 'multiple_choice',
    CHECKBOXES = 'checkboxes',
    YES_NO = 'yes_no',
    RATING_SCALE = 'rating_scale',
    IMAGE_UPLOAD = 'image_upload',
    DATE_PICKER = 'date_picker',
    TIME_PICKER = 'time_picker',
    NUMBER_INPUT = 'number_input',
    PHONE_INPUT = 'phone_input',
    SECTION_TITLE = 'section_title',
    TEXT_BLOCK = 'text_block',
    DIVIDER = 'divider'
}

export interface ComponentOption {
    id: string;
    label: string;
    value: string;
}

export type SurveyResponseValue = 
    | string              // TEXT_INPUT, TEXTAREA, EMAIL_INPUT, PHONE_INPUT, DATE_PICKER, TIME_PICKER
    | number              // NUMBER_INPUT, RATING_SCALE
    | boolean             // YES_NO
    | string[]            // MULTIPLE_CHOICE (single), CHECKBOXES (multiple)
    | File[]              // IMAGE_UPLOAD
    | null                // Empty/unset values
    | undefined;          // Optional fields

export interface SurveyComponent {
    id: string; 
    type: ComponentType;
    label: string;
    description?: string;
    placeholder?: string;
    required: boolean;
    position: number;
    
    // Component-specific configuration
    properties?: {
        // Text inputs
        minLength?: number;
        maxLength?: number;
        multiline?: boolean;
        rows?: number;
        
        // Multiple choice / checkboxes
        options?: ComponentOption[];
        multiSelect?: boolean;
        
        // Rating scales
        scale?: number;
        scaleLabels?: string[];
        scaleType?: 'stars' | 'numbers' | 'emoji';
        
        // Image upload
        maxFiles?: number;
        maxFileSize?: number; // MB
        allowedTypes?: string[];
        
        // Date/time
        minDate?: Timestamp;
        maxDate?: Timestamp;
        includeTime?: boolean;
        
        // Display components
        text?: string;
        variant?: 'h2' | 'h3' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2';
        align?: 'left' | 'center' | 'right';
        
        // Legacy properties for backwards compatibility
        min?: number;
        max?: number;
        step?: number;
        multiple?: boolean;
        allowedFileTypes?: string[];
        labelVariant?: 'subtitle2' | 'subtitle1' | 'h6';
    };
    
    // Standard properties
    options?: ComponentOption[];
    defaultValue?: any;
}

export interface SurveyTemplatDbData {
    ID: string;
    partnerID: string;
    name: string;
    description?: string;
    updatedAt: Timestamp;
    createdBy: {
        email: string;
        name: string;
    };
    
    components: SurveyComponent[];
}

export type SurveyTemplate = Omit<SurveyTemplatDbData, "updatedAt"> & {
    updatedAt: string;
}

export interface SurveyResponse {
    ID: string;
    templateID: string;
    missionID: string;
    addressID: string;
    responses: { [fieldId: string]: any };
    submittedAt: Timestamp;
    submittedBy: {
        email: string;
        name: string;
    };
    partnerID: string;
}

// Survey builder state types
export interface SurveyBuilderState {
    selectedField: SurveyComponent | null;
    draggedField: SurveyComponent | null;
    previewMode: boolean;
    unsavedChanges: boolean;
}

export interface FieldTypeDefinition {
    type: ComponentType;
    label: string;
    icon: string;
    description: string;
    defaultProperties: Partial<SurveyComponent>;
}

// Translatable field type definitions factory
export const createFieldTypeDefinitions = (t: (key: string) => string): FieldTypeDefinition[] => [
    {
        type: ComponentType.YES_NO,
        label: t('field_types.yes_no.label'),
        icon: 'check_circle',
        description: t('field_types.yes_no.description'),
        defaultProperties: {
            required: false,
            position: 0,

        }
    },
    {
        type: ComponentType.MULTIPLE_CHOICE,
        label: t('field_types.multiple_choice.label'),
        icon: 'radio_button_checked',
        description: t('field_types.multiple_choice.description'),
        defaultProperties: {
            required: false,
            position: 0,
            options: [
                { id: '1', label: t('default_options.option_1'), value: 'option1' },
                { id: '2', label: t('default_options.option_2'), value: 'option2' }
            ]
        }
    },
    {
        type: ComponentType.CHECKBOXES,
        label: t('field_types.checkboxes.label'),
        icon: 'check_box',
        description: t('field_types.checkboxes.description'),
        defaultProperties: {
            required: false,
            position: 0,
            options: [
                { id: '1', label: t('default_options.option_1'), value: 'option1' },
                { id: '2', label: t('default_options.option_2'), value: 'option2' }
            ]
        }
    },
    {
        type: ComponentType.TEXT_INPUT,
        label: t('field_types.text_input.label'),
        icon: 'text_fields',
        description: t('field_types.text_input.description'),
        defaultProperties: {
            required: false,
            position: 0,

        }
    },
    {
        type: ComponentType.TEXTAREA,
        label: t('field_types.textarea.label'),
        icon: 'notes',
        description: t('field_types.textarea.description'),
        defaultProperties: {
            required: false,
            position: 0,
            properties: { rows: 4 },

        }
    },
    {
        type: ComponentType.NUMBER_INPUT,
        label: t('field_types.number_input.label'),
        icon: 'pin',
        description: t('field_types.number_input.description'),
        defaultProperties: {
            required: false,
            position: 0,
            properties: { step: 1 }
        }
    },
    {
        type: ComponentType.RATING_SCALE,
        label: t('field_types.rating_scale.label'),
        icon: 'star',
        description: t('field_types.rating_scale.description'),
        defaultProperties: {
            required: false,
            position: 0,
            properties: { 
                scale: 5,
                scaleLabels: [
                    t('rating_labels.poor'),
                    t('rating_labels.fair'),
                    t('rating_labels.good'),
                    t('rating_labels.very_good'),
                    t('rating_labels.excellent')
                ]
            }
        }
    },
    {
        type: ComponentType.IMAGE_UPLOAD,
        label: t('field_types.image_upload.label'),
        icon: 'image',
        description: t('field_types.image_upload.description'),
        defaultProperties: {
            required: false,
            position: 0,
            properties: {
                multiple: true,
                allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif'],
                maxFileSize: 5
            }
        }
    },
    {
        type: ComponentType.DATE_PICKER,
        label: t('field_types.date_picker.label'),
        icon: 'calendar_today',
        description: t('field_types.date_picker.description'),
        defaultProperties: {
            required: false,
            position: 0
        }
    },
    {
        type: ComponentType.TIME_PICKER,
        label: t('field_types.time_picker.label'),
        icon: 'access_time',
        description: t('field_types.time_picker.description'),
        defaultProperties: {
            required: false,
            position: 0
        }
    },
    {
        type: ComponentType.SECTION_TITLE,
        label: t('field_types.section_title.label'),
        icon: 'title',
        description: t('field_types.section_title.description'),
        defaultProperties: {
            required: false,
            position: 0,
            properties: {
                text: t('default_text.section_title'),
                variant: 'h3',
                align: 'left'
            }
        }
    },
    {
        type: ComponentType.TEXT_BLOCK,
        label: t('field_types.text_block.label'),
        icon: 'description',
        description: t('field_types.text_block.description'),
        defaultProperties: {
            required: false,
            position: 0,
            properties: {
                text: t('default_text.text_block'),
                variant: 'body1',
                align: 'left'
            }
        }
    },
    // New field types
    {
        type: ComponentType.PHONE_INPUT,
        label: t('field_types.phone_input.label'),
        icon: 'phone',
        description: t('field_types.phone_input.description'),
        defaultProperties: {
            required: false,
            position: 0
        }
    },
    {
        type: ComponentType.DIVIDER,
        label: t('field_types.divider.label'),
        icon: 'horizontal_rule',
        description: t('field_types.divider.description'),
        defaultProperties: {
            required: false,
            position: 0
        }
    }
];

export const getFieldTypeDefinitions = (): FieldTypeDefinition[] => {
    const t = (key: string) => key; 
    
    return createFieldTypeDefinitions(t);
};
